import type {AxiosRequestConfig} from 'axios';
import {EntityService} from '../EntityService';
import {getLocale} from '../../shared/language';

type InvoiceSettingsRequestConfig = AxiosRequestConfig & {
  // MI18N: Cho phép reload labels âm thầm khi đổi language, không bật global loading.
  skipLoading?: boolean;
};

type GetInvoiceSettingsOptions = {
  /** Bỏ qua cache để lấy lại cấu hình mới nhất từ backend khi cần. */
  force?: boolean;
};

export class RegisterInvoiceService extends EntityService {

  static entity = 'app/register-invoice';
  /** Cache cấu hình theo locale và registerInvoiceId để không dùng nhầm label của ngôn ngữ khác. */
  private static invoiceSettingsCache: { [id: string]: any } = {};
  /** Lưu request đang chạy theo locale và registerInvoiceId để các component gọi song song dùng chung một Promise. */
  private static invoiceSettingsRequests: { [id: string]: Promise<any> } = {};

  /** Dùng cùng locale mà Axios gắn vào Accept-Language cho mỗi request. */
  private static getInvoiceSettingsCacheKey(id: string): string {
    return `${getLocale()}::${id}`;
  }

  static getInvoiceSettings(
    id: string,
    config?: InvoiceSettingsRequestConfig,
    options: GetInvoiceSettingsOptions = {}): Promise<any> {
    const cacheKey = this.getInvoiceSettingsCacheKey(id);

    // Nếu đã có dữ liệu trong cache thì trả ngay, không phát sinh request mới.
    if (!options.force && Object.prototype.hasOwnProperty.call(this.invoiceSettingsCache, cacheKey)) {
      return Promise.resolve(this.invoiceSettingsCache[cacheKey]);
    }

    // Nếu request cùng id đang chạy thì tái sử dụng Promise hiện có.
    if (!options.force && this.invoiceSettingsRequests[cacheKey]) {
      return this.invoiceSettingsRequests[cacheKey];
    }

    // Chỉ cache response thành công; request lỗi được xóa để lần sau có thể gọi lại.
    const request = this.axios
      .get(`${this.entity}/tenant-invoice-settings/${id}`, config)

      .then(res => {
        this.invoiceSettingsCache[cacheKey] = res.data;
        delete this.invoiceSettingsRequests[cacheKey];
        return res.data;
      })
      .catch(error => {
        delete this.invoiceSettingsRequests[cacheKey];
        throw error;
      });

    this.invoiceSettingsRequests[cacheKey] = request;
    return request;
  }

  /** Xóa cache khi cấu hình đăng ký thay đổi hoặc cần buộc reload dữ liệu mới. */
  static clearInvoiceSettingsCache(id?: string): void {
    if (id) {
      // Xóa toàn bộ locale của invoice để lần tải kế tiếp luôn lấy cấu hình mới.
      const cacheKeySuffix = `::${id}`;
      Object.keys(this.invoiceSettingsCache).forEach(cacheKey => {
        if (cacheKey.endsWith(cacheKeySuffix)) {
          delete this.invoiceSettingsCache[cacheKey];
        }
      });
      Object.keys(this.invoiceSettingsRequests).forEach(cacheKey => {
        if (cacheKey.endsWith(cacheKeySuffix)) {
          delete this.invoiceSettingsRequests[cacheKey];
        }
      });
      return;
    }

    this.invoiceSettingsCache = {};
    this.invoiceSettingsRequests = {};
  }

  static getUsingOfYearList(params: any): Promise<any> {
    return this.axios.get(`${this.entity}/using-of-year-list`, {params}).then(res => res.data);
  }

  static getUsingList(params: any): Promise<any> {
    return this.axios.get(`${this.entity}/using-list`, {params}).then(res => res.data);
  }

  static getCounterStatusInvoice(params: any): Promise<any> {
    return this.axios.get(`${this.entity}/count-invoice-list`, {params}).then(res => res.data);
  }

  static getPermisionList(params: any): Promise<any> {
    return this.axios.get(`${this.entity}/using-permission`, {params}).then(res => res.data);
  }

  static getInvoiceSerialByYear(): Promise<any> {
    return this.axios.get(`${this.entity}/invoice-serial-by-year`).then(res => res.data);
  }

  static copyRegisterInvoice(data: any): Promise<any> {
    return this.axios.post(`${this.entity}/copy-register-invoice`, data).then(res => res.data);
  }

}
